<template>
  <div class="row g-2 justify-content-center my-5">
    <div class="col-xxl-6 col-xl-7 col-lg-8 col-md-11">
      <p>22. Januar 2026 von Theo Döllmann</p>
      <h1>Welcher Bahnhof ist der Richtige?</h1>
      <p>
        Vielen Menschen ist bekannt, dass es nicht nur einen Berliner Hauptbahnhof gibt. Betrieblich
        gesehen ist der Bahnhof in
        <span class="fw-lighter mx-1">Berlin Hbf</span>,
        <span class="fw-lighter mx-1">Berlin Hbf (tief)</span> und
        <span class="fw-lighter mx-1">Berlin Hbf (S-Bahn)</span> aufgeteilt. Intern mag das Sinn
        machen, aber auf der Reise nach Berlin ist es den meisten dann doch reichlich egal, ob man
        unten, oben oder mit der S-Bahn ankommt. Daher habe ich mir auch vor kurzem bei
        Bahn-Vorhersage die Frage gestellt, welchen der Bahnhöfe ich in der Bahnhofssuche anzeigen
        möchte. Bisher waren es einfach alle, sortiert nach der Anzahl der Zughalte an diesem Halt.
      </p>
      <p>
        Bleiben wir einmal am Beispiel des Berliner Hauptbahnhofs: Tatsächlich gibt es neben den
        drei schon benannten Haltestellen auch noch zwei weitere, nämlich die Bushaltestellen
        <span class="fw-lighter mx-1">Berlin Hbf (Invalidenstraße)</span> und
        <span class="fw-lighter mx-1">Berlin Hbf (Washingtonplatz)</span>. In meinem von der Bahn
        gesammelten Stations-Datensatz verweisen <span class="fw-lighter mx-1">Berlin Hbf</span>,
        <span class="fw-lighter mx-1">Berlin Hbf (tief)</span>,
        <span class="fw-lighter mx-1">Berlin Hbf (S-Bahn)</span> und
        <span class="fw-lighter mx-1">Berlin Hbf (Invalidenstraße)</span>
        jeweils auf einander und gehören somit offiziell zusammen. Die Haltestelle
        <span class="fw-lighter mx-1">Berlin Hbf (Washingtonplatz)</span> ist laut meinen Daten
        Single. Stellt man das Geflecht grafisch als Graph dar, sieht es so aus wie in
        <a href="#figure_1" class="link-light fst-italic">Abbildung 1</a>.
      </p>
      <div style="max-width: 248pt; margin: auto">
        <figure id="figure_1" class="figure">
          <img
            :src="berlinHbf"
            alt="Ein Graph des Berliner Hauptbahnhofs und seiner zugehörigen Haltestellen. Vier Knoten sind miteinander verbunden."
            class="figure-img img-fluid bg-dark rounded"
          />
          <figcaption class="figure-caption">
            Abbildung 1: Die Haltestellen des Berliner Hauptbahnhofs und deren Verbindungen
            untereinander. Daten gesammelt von der Deutschen Bahn.
          </figcaption>
        </figure>
      </div>
      <p>
        In diesem Fall ist die Wahl relativ einfach: Statt alle fünf Haltestellen anzuzeigen, zeige
        ich nur
        <span class="fw-lighter mx-1">Berlin Hbf</span> und
        <span class="fw-lighter mx-1">Berlin Hbf (Washingtonplatz)</span> an. Die übrigen gelten als
        Unterstationen und werden versteckt.
      </p>
      <p>
        Allerdings gibt es noch viele weitere Fälle und Ausnahmen. Relativ typisch ist es wie in
        <a href="#figure_2" class="link-light fst-italic">Abbildung 2</a> beim Kasseler
        Hauptbahnhof, dass nicht alle Haltestellen untereinander verbunden sind. In anderen Worten
        ist der Graph den Bahnhofs unvollständig.
      </p>
      <div>
        <figure id="figure_2" class="figure">
          <img
            :src="kasselHbf"
            alt="Ein Graph des Kasseler Hauptbahnhofs und seiner zugehörigen Haltestellen."
            class="figure-img img-fluid bg-dark rounded"
          />
          <figcaption class="figure-caption">
            Abbildung 2: Die Haltestellen des Kasseler Hauptbahnhofs und deren Verbindungen
            untereinander, allerdings sind nicht alle Haltestellen untereinander verbunden. Daten
            gesammelt von der Deutschen Bahn.
          </figcaption>
        </figure>
      </div>
      <p>
        Teilweise gehört laut den Daten auch Halt A zu Halt B, aber nicht umgekehrt. Weitere
        Beispiele folgen für Interessierte am Schluss.
      </p>
      <h2>Was macht jetzt Bahn-Vorhersage?</h2>
      <p>
        Wir fügen alle Verbindungen zwischen den verschiedenen Bahnhöfen in einen Graphen ein. Da
        unser Graph keine Richtung hat, gelten zwei Halte schon dann als verbunden, wenn nur ein
        Halt diese Verbindung angibt. Dann schauen wir uns die Komponenten des Graphs an, also die
        Gruppen an Bahnhöfen, die miteinander verbunden sind, auch wenn die Verbindungen nicht
        direkt sind. Für jede Komponente wählen wir den Bahnhof mit dem kürzesten Namen.
      </p>
      <p>
        So wie ich die Bahn kenne, gibt es sicherlich auch bei diesem Vorgehen noch Fehler,
        allerdings ist mir bisher noch keiner bekannt.
      </p>
      <h2>Weitere Beispiele</h2>
      <p>
        Es folgen einige weitere Beispiele von komplexeren Bahnhofskonstrukten. Wir beginnen mit
        Heilbronn, wo es mehrere Schreibweisen für den Willi-Brandt Platz gibt, aber nicht alle
        Schreibweisen sind mit dem Busbahnhof verbunden.
      </p>
      <div style="max-width: 710pt; margin: auto">
        <figure id="figure_3" class="figure">
          <img
            :src="heilbronnHbf"
            alt="Ein Graph des Heilbronner Hauptbahnhofs und seiner zugehörigen Haltestellen."
            class="figure-img img-fluid bg-dark rounded"
          />
          <figcaption class="figure-caption">Abbildung 3: Heilbronn Hbf.</figcaption>
        </figure>
      </div>
      <p>
        Beim Hauptbahnhof in Karlsruhe hingegen sind alle Haltestellen miteinander verbunden, auch
        die verschiedenen Schreibweisen des Bahnhofsvorplatzes.
      </p>
      <div style="max-width: 371pt; margin: auto">
        <figure id="figure_4" class="figure">
          <img
            :src="karlsruheHbf"
            alt="Ein Graph des Karlsruher Hauptbahnhofs und seiner zugehörigen Haltestellen."
            class="figure-img img-fluid bg-dark rounded"
          />
          <figcaption class="figure-caption">Abbildung 4: Karlsruhe Hbf.</figcaption>
        </figure>
      </div>
      <p>
        Auch die Kleinstadt Konz mit knapp 20.000 Einwohnern besitzt schon ein komplexes
        Bahnhofssystem.
      </p>
      <div style="max-width: 191pt; margin: auto">
        <figure id="figure_5" class="figure">
          <img
            :src="konz"
            alt="Ein Graph des Bahnhofs in Konz und seiner zugehörigen Haltestellen."
            class="figure-img img-fluid bg-dark rounded"
          />
          <figcaption class="figure-caption">Abbildung 5: Konz.</figcaption>
        </figure>
      </div>
      <p>
        In Paris ist die Situation besonders komisch, denn laut Bahn gehören die tatsächlich
        verschiedenen Bahnhöfe Est, Nord und Magenta alle zusammen. Der Bahnhof
        <span class="fw-lighter mx-1">Paris Magenta</span>, der genau zwischen den beiden anderen
        Bahnhöfen liegt ist aber nur mit <span class="fw-lighter mx-1">Paris Nord</span> verbunden,
        nicht mit dem ebenso nahem <span class="fw-lighter mx-1">Paris Est</span>.
      </p>
      <div style="max-width: 187pt; margin: auto">
        <figure id="figure_6" class="figure">
          <img
            :src="paris"
            alt="Ein Graph eines Teils der Pariser Bahnhöfe."
            class="figure-img img-fluid bg-dark rounded"
          />
          <figcaption class="figure-caption">
            Abbildung 6: Paris. Interessanterweise liegt Paris Magenta genau zwischen Paris Nord und
            Paris Est, aber die Bahn scheint das nicht zu wissen.
          </figcaption>
        </figure>
      </div>
      <p>In Potsdam gibt es besonders viele Haltestellen für die S-Bahn.</p>
      <div style="max-width: 731pt; margin: auto">
        <figure id="figure_7" class="figure">
          <img
            :src="potsdamHbf"
            alt="Ein Graph des Potsdamer Hauptbahnhofs und seiner zugehörigen Haltestellen."
            class="figure-img img-fluid bg-dark rounded"
          />
          <figcaption class="figure-caption">Abbildung 7: Potsdam Hbf.</figcaption>
        </figure>
      </div>
      <p>
        Auch in der 5.000 Seelen Gemeinde Sulzbach an der Murr gibt es einige Komplexität.
        Interessanterweise geht hier auch Google Maps davon aus, dass am Markplatz auch Züge fahren
        und hat die Haltestelle dort fernab von jeglichen Schienen als Bahnhof eingezeichnet.
      </p>
      <div style="max-width: 482pt; margin: auto">
        <figure id="figure_8" class="figure">
          <img
            :src="sulzbachMurr"
            alt="Ein Graph des Bahnhofs Sulzbach (Murr) und seiner zugehörigen Haltestellen."
            class="figure-img img-fluid bg-dark rounded"
          />
          <figcaption class="figure-caption">Abbildung 8: Sulzbach (Murr).</figcaption>
        </figure>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
definePageMeta({ layout: 'default' })
import berlinHbf from '~/assets/img/blog/Berlin Hbf.svg'
import heilbronnHbf from '~/assets/img/blog/Heilbronn Hbf.svg'
import karlsruheHbf from '~/assets/img/blog/Karlsruhe Hbf.svg'
import kasselHbf from '~/assets/img/blog/Kassel Hbf.svg'
import konz from '~/assets/img/blog/Konz.svg'
import paris from '~/assets/img/blog/Paris.svg'
import potsdamHbf from '~/assets/img/blog/Potsdam Hbf.svg'
import sulzbachMurr from '~/assets/img/blog/Sulzbach(Murr).svg'

useHead({
  title: 'Blog: Welcher Bahnhof ist der Richtige?',
  meta: [
    {
      name: 'description',
      content:
        'Blog: Diskussion darüber, welcher Bahnhof in Fällen mit mehreren Betriebsbahnhöfen angezeigt werden sollte.'
    }
  ]
})
</script>
